import { useEffect, useRef, useState } from "react";
import { Power, RefreshCw } from "lucide-react";
import type { IntegrationProvisioningClient } from "./integrationSetupClient";
import { IntegrationOperationStatus } from "./IntegrationOperationStatus";
import type { MindsetIntegrationProvisioningOperation } from "@mindset/api-client";
import { IntegrationSdkDownload } from "./IntegrationSdkDownload";

interface Props { companyId: string; userId: string; applicationId: string; environment?: "sandbox" | "production"; client: IntegrationProvisioningClient; onChanged?: () => void }
interface Intent { requestKey: string; operationId?: string }
export function IntegrationActivation(props: Props) {
  return <Activation key={`${props.companyId}:${props.userId}:${props.applicationId}:${props.environment ?? ""}`} {...props} />;
}
function Activation({ companyId, userId, applicationId, environment, client, onChanged }: Props) {
  const key = `mindset-activation:${companyId}:${userId}:${applicationId}`;
  const [intent, setIntent] = useState<Intent | null>(null);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [storageFailed, setStorageFailed] = useState(false);
  const [recovering, setRecovering] = useState(Boolean(client.currentProvisioning));
  const [recoveryFailed, setRecoveryFailed] = useState(false);
  const [recoveryVersion, setRecoveryVersion] = useState(0);
  const [recoveredId, setRecoveredId] = useState<string | null>(null);
  const [operationState, setOperationState] = useState<MindsetIntegrationProvisioningOperation["state"]>();
  const canStartNew = operationState === "FAILED" || operationState === "CANCELED";
  const active = useRef(false), pending = useRef(false), saved = useRef<Intent | null>(null);
  useEffect(() => {
    active.current = true;
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        if (raw.length > 2048) throw new Error();
        const value: unknown = JSON.parse(raw);
        if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error();
        const candidate = value as Record<string, unknown>;
        if (Object.keys(candidate).some(field => !["requestKey", "operationId"].includes(field)) ||
          typeof candidate.requestKey !== "string" || !candidate.requestKey || candidate.requestKey.length > 255 ||
          (candidate.operationId !== undefined && (typeof candidate.operationId !== "string" || !candidate.operationId || candidate.operationId.length > 255))) throw new Error();
        saved.current = candidate as unknown as Intent;
        setIntent(saved.current);
      }
      setReady(true);
    } catch { setStorageFailed(true); setError("Saved activation could not be recovered. Check application activity before retrying."); }
    return () => { active.current = false; };
  }, [key]);
  useEffect(() => {
    if (!client.currentProvisioning) return;
    let current = true;
    setRecovering(true); setRecoveryFailed(false);
    void Promise.resolve().then(() => client.currentProvisioning!(companyId, userId, applicationId)).then(operation => {
      if (!current) return;
      if (operation !== null && (!operation.id || operation.companyId !== companyId || operation.applicationId !== applicationId)) throw new Error();
      // History cannot identify an unconfirmed request: replay its saved key
      // before treating another operation as this tab's activation result.
      setRecoveredId(saved.current && !saved.current.operationId ? null : operation?.id ?? null);
    }).catch(() => { if (current) setRecoveryFailed(true); })
      .finally(() => { if (current) setRecovering(false); });
    return () => { current = false; };
  }, [companyId, userId, applicationId, client.currentProvisioning, recoveryVersion]);
  async function activate(newAttempt = false) {
    if (!ready || pending.current || storageFailed || recovering || recoveryFailed ||
      (newAttempt ? !canStartNew : Boolean(saved.current?.operationId || recoveredId))) return;
    pending.current = true; setBusy(true); setError("");
    try {
      const value = (newAttempt ? null : saved.current) ?? { requestKey: crypto.randomUUID() };
      try { sessionStorage.setItem(key, JSON.stringify(value)); }
      catch { setStorageFailed(true); throw new Error(); }
      saved.current = value; setIntent(value);
      setRecoveredId(null); setOperationState(undefined);
      const operation = await client.requestProvisioning(companyId, userId, applicationId, value.requestKey);
      if (!active.current) return;
      if (!operation.id || operation.companyId !== companyId || operation.applicationId !== applicationId) throw new Error();
      const completed = { ...value, operationId: operation.id };
      saved.current = completed; setIntent(completed);
      try { sessionStorage.setItem(key, JSON.stringify(completed)); }
      catch { setError("Activation was requested, but its status could not be saved in this tab."); }
      onChanged?.();
    } catch { if (active.current) setError("Activation could not be confirmed. A verified subscription and application setup are required. Retry uses the same request."); }
    finally { pending.current = false; if (active.current) setBusy(false); }
  }
  return <section className="integration-activation" aria-label="Application activation">
    <h3>Activation</h3>
    {recovering ? <p role="status">Checking existing activation...</p> : recoveryFailed ? <>
      <p role="alert">Existing activation could not be recovered.</p>
      <button type="button" className="portal-button" onClick={() => setRecoveryVersion(value => value + 1)}><RefreshCw size={16} />Retry recovery</button>
    </> : (recoveredId ?? intent?.operationId) ? <IntegrationOperationStatus companyId={companyId} userId={userId} operationId={(recoveredId ?? intent?.operationId)!}
      applicationId={applicationId} read={client.getProvisioning} onState={setOperationState} autoRefresh /> : <button type="button" className="portal-button portal-button--primary"
      disabled={!ready || busy || storageFailed} onClick={() => void activate()}>
      {intent ? <RefreshCw size={16} /> : <Power size={16} />}{busy ? "Requesting activation..." : intent ? "Retry activation" : "Activate application"}
    </button>}
    {canStartNew && !recovering && !recoveryFailed ? <button type="button" className="portal-button"
      disabled={!ready || busy || storageFailed} onClick={() => void activate(true)}><RefreshCw size={16} />Start new activation attempt</button> : null}
    {operationState === "COMPLETED" && !recovering && !recoveryFailed && client.sdkConfiguration ?
      <IntegrationSdkDownload companyId={companyId} userId={userId} applicationId={applicationId} environment={environment}
        read={client.sdkConfiguration} readRelease={client.sdkRelease} createDownload={client.sdkDownload} /> : null}
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}
